import React, { useState, useEffect } from 'react';
import { X, Loader, AlertCircle, TrendingUp } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import RateLimitModal from './RateLimitModal';
import { supabase } from '../../../lib/supabase';
import { API_ENDPOINTS } from '../../../config.js';
import './UniversityAnalysisModal.css';

const UniversityAnalysisModal = ({ isOpen, onClose, onAnalysisComplete, userId, currentUniversityId = null }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [universities, setUniversities] = useState([]);
  const [filteredUniversities, setFilteredUniversities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [showRateLimitModal, setShowRateLimitModal] = useState(false);
  const [analysesRemaining, setAnalysesRemaining] = useState(null);

  // Fetch universities on mount
  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('universities')
          .select('id, name, country, admit_rate');

        if (error) throw error;
        setUniversities(data || []);
      } catch (err) {
        console.warn('Could not fetch universities:', err);
        setUniversities([]);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchUniversities();
    }
  }, [isOpen]);

  // Filter universities based on search query
  useEffect(() => {
    if (searchQuery.length > 0) {
      const filtered = universities.filter(uni =>
        uni.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        uni.country?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUniversities(filtered.slice(0, 10)); // Limit to 10 results
    } else {
      setFilteredUniversities([]);
    }
  }, [searchQuery, universities]);

  // Pre-select university if provided
  useEffect(() => {
    if (currentUniversityId && universities.length > 0) {
      const uni = universities.find(u => u.id === currentUniversityId);
      if (uni) {
        setSelectedUniversity(uni);
        setSearchQuery(uni.name);
        setSearchOpen(false);
      }
    }
  }, [currentUniversityId, universities]);

  const handleSelectUniversity = (university) => {
    setSelectedUniversity(university);
    setSearchQuery(university.name);
    setSearchOpen(false);
  };

  const handleAnalyze = async () => {
    if (!selectedUniversity || !userId) {
      setError('Please select a university');
      return;
    }

    setAnalyzing(true);
    setError(null);

    try {
      const response = await fetch(API_ENDPOINTS.analyzeAdmission, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          universityId: selectedUniversity.id,
          universityName: selectedUniversity.name
        })
      });

      const data = await response.json();

      // Handle rate limit (429)
      if (response.status === 429) {
        setRateLimitInfo({
          resetTime: data.details?.reset_at,
          dailyQuota: data.details?.limit || 5,
          analysesRemaining: 0
        });
        setShowRateLimitModal(true);
        setAnalyzing(false);
        return;
      }

      if (!response.ok) {
        throw new Error(data.error || 'Analysis failed');
      }

      // Update remaining analyses from response
      if (data.rateLimit) {
        setAnalysesRemaining(data.rateLimit.analysesRemaining);
        setRateLimitInfo(data.rateLimit);
      }

      if (onAnalysisComplete) {
        onAnalysisComplete(data.data);
      }

      // Close modal after successful analysis
      setTimeout(() => {
        onClose();
      }, 1000);

    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message || 'Failed to analyze admission chances');
    } finally {
      setAnalyzing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="analysis-modal-overlay" onClick={onClose}>
      <div className="analysis-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="analysis-modal-header">
          <div className="analysis-modal-title-section">
            <TrendingUp className="analysis-modal-icon" />
            <div>
              <h2 className="analysis-modal-title">Analyse Your Chances</h2>
              <p className="analysis-modal-subtitle">Get AI-powered insights on your admission probability</p>
            </div>
          </div>
          <button
            className="analysis-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="analysis-modal-body">
          {error && (
            <div className="analysis-error-banner">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {!selectedUniversity ? (
            <>
              <div className="analysis-form-group">
                <label className="analysis-form-label">Search University</label>
                <div className="analysis-search-container">
                  <Input
                    type="text"
                    placeholder="Search by university name or country..."
                    value={searchQuery}
                    onChange={e => {
                      setSearchQuery(e.target.value);
                      setSearchOpen(true);
                    }}
                    onFocus={() => setSearchOpen(true)}
                    disabled={analyzing}
                  />
                  {loading && <Loader className="analysis-search-spinner" />}
                </div>

                {/* Dropdown Results */}
                {searchOpen && filteredUniversities.length > 0 && (
                  <div className="analysis-search-dropdown">
                    {filteredUniversities.map(uni => (
                      <div
                        key={uni.id}
                        className={`analysis-dropdown-item ${selectedUniversity?.id === uni.id ? 'active' : ''}`}
                        onClick={() => handleSelectUniversity(uni)}
                      >
                        <div className="analysis-dropdown-name">{uni.name}</div>
                        <div className="analysis-dropdown-meta">
                          {uni.country && <span>{uni.country}</span>}
                          {uni.admit_rate && <span>{uni.admit_rate}% admit rate</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="analysis-info-box">
                <h4 className="analysis-info-title">How it works</h4>
                <ul className="analysis-info-list">
                  <li>
                    <span className="analysis-info-number">1</span>
                    Our AI analyzes your complete portfolio from documents
                  </li>
                  <li>
                    <span className="analysis-info-number">2</span>
                    Compares against the university's admission requirements
                  </li>
                  <li>
                    <span className="analysis-info-number">3</span>
                    Provides personalized insights and action plan
                  </li>
                  <li>
                    <span className="analysis-info-number">4</span>
                    Results are saved for future reference
                  </li>
                </ul>
              </div>
            </>
          ) : (
            <div className="analysis-selected-section">
              <div className="analysis-selection-confirmed">
                <div style={{ fontSize: '32px', marginBottom: '16px' }}>✓</div>
                <h3 className="analysis-selected-title">Ready to Analyze</h3>
                <div className="analysis-selected-card">
                  <h2>{selectedUniversity.name}</h2>
                  {selectedUniversity.country && (
                    <p className="analysis-selected-location">{selectedUniversity.country}</p>
                  )}
                  {selectedUniversity.admit_rate && (
                    <p className="analysis-selected-stats">Admit Rate: {selectedUniversity.admit_rate}%</p>
                  )}
                </div>
              </div>

              <div className="analysis-info-box">
                <h4 className="analysis-info-title">What happens next</h4>
                <ul className="analysis-info-list">
                  <li>
                    <span className="analysis-info-number">1</span>
                    AI analyzes your complete portfolio
                  </li>
                  <li>
                    <span className="analysis-info-number">2</span>
                    Compares your profile against admission requirements
                  </li>
                  <li>
                    <span className="analysis-info-number">3</span>
                    Generates personalized insights
                  </li>
                  <li>
                    <span className="analysis-info-number">4</span>
                    Results saved to your history
                  </li>
                </ul>
              </div>

              <button
                className="analysis-change-university"
                onClick={() => {
                  setSelectedUniversity(null);
                  setSearchQuery('');
                  setSearchOpen(true);
                }}
              >
                ← Choose Different University
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="analysis-modal-footer">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={analyzing}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAnalyze}
            disabled={!selectedUniversity || analyzing}
            loading={analyzing}
          >
            {analyzing ? 'Analyzing...' : 'Start Analysis'}
          </Button>
        </div>

        {/* Analyses Remaining Display */}
        {analysesRemaining !== null && analysesRemaining >= 0 && (
          <div className="analysis-modal-quota">
            <p className="text-xs text-muted-foreground">
              <strong>{analysesRemaining}</strong> analyses remaining today
            </p>
          </div>
        )}
      </div>

      {/* Rate Limit Modal */}
      <RateLimitModal
        isOpen={showRateLimitModal}
        onClose={() => setShowRateLimitModal(false)}
        resetTime={rateLimitInfo?.resetTime}
        dailyQuota={rateLimitInfo?.dailyQuota || 5}
      />
    </div>
  );
};

export default UniversityAnalysisModal;
